import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { clientsEndpoint, jobsEndpoint } from "../constants";
import CandidateSubmissionImage from "../assets/Candidate Submission.jpg";

const CandidateToClientSubmission = () => {
  const { applicant_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get("client");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [applicant, setApplicant] = useState(null);
  const [clients, setClients] = useState([]);
  const [roles, setRoles] = useState([]);

  const [formData, setFormData] = useState({
    clientId: "",
    roleId: "",
    hideContactDetails: true,
    includeShortlistLink: true,
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, [applicant_id]);

  useEffect(() => {
    if (clientIdFromUrl && clients.length > 0 && !formData.clientId) {
      setFormData(prev => ({ ...prev, clientId: clientIdFromUrl }));
    }
  }, [clientIdFromUrl, clients]);

  useEffect(() => {
    if (formData.clientId) {
      fetchRoles(formData.clientId);
    } else {
      setRoles([]);
      setFormData(prev => ({ ...prev, roleId: "" }));
    }
  }, [formData.clientId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
      };

      const requests = [fetch(clientsEndpoint, { headers })];

      if (applicant_id) {
        requests.push(fetch(`${jobsEndpoint}applicant_details/${applicant_id}/`, { headers }));
      }

      const responses = await Promise.all(requests);
      const clientsData = await responses[0].json();
      setClients(clientsData.results || clientsData || []);

      if (applicant_id && responses[1]) {
        const applicantData = await responses[1].json();
        setApplicant(applicantData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async (clientId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${jobsEndpoint}?client=${clientId}`, {
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      setRoles(data.results || data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    const validTypes = ["image/svg+xml", "image/png", "image/jpeg", "image/gif", "application/pdf"];
    if (!validTypes.includes(file.type)) {
      alert("Please upload SVG, PNG, JPG, GIF or PDF file");
      return;
    }
    setUploadedFile(file);
  };

  const removeFile = () => {
    setUploadedFile(null);
  };

  const handleSubmit = async () => {
    if (!formData.clientId || !formData.roleId) {
      alert("Please select client and role");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const formPayload = new FormData();
      formPayload.append("applicant_id", applicant_id);
      formPayload.append("client_id", formData.clientId);
      formPayload.append("role_id", formData.roleId);
      formPayload.append("hide_contact_details", formData.hideContactDetails);
      formPayload.append("include_shortlist_link", formData.includeShortlistLink);
      if (uploadedFile) {
        formPayload.append("branded_cv", uploadedFile);
      }

      const response = await fetch(`${jobsEndpoint}submissions/`, {
        method: "POST",
        headers: {
          Authorization: `Token ${token}`,
        },
        body: formPayload,
      });

      if (response.ok) {
        alert("Submission sent successfully!");
        navigate(-1);
      } else {
        throw new Error("Failed to send submission");
      }
    } catch (error) {
      console.error("Error submitting:", error);
      alert("Failed to send submission");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#19bfb7]"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-500 hover:text-gray-700 text-sm"
        >
          ← Back
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Candidate to Client Submission</h1>
          <p className="text-sm text-gray-500 mb-6">Enter the details below to compose your submission</p>

          <div className="space-y-6">
            {applicant && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-2">Candidate</label>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-gray-600 font-semibold">
                      {(applicant.username || "C")[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{applicant.username || "Candidate"}</p>
                    <p className="text-xs text-gray-500">{applicant.job?.title || "Applicant"}</p>
                  </div>
                </div>
              </div>
            )}

            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive ? "border-[#19bfb7] bg-teal-50" : "border-gray-300"
              }`}
            >
              {uploadedFile ? (
                <div className="flex items-center justify-center gap-3">
                  <svg className="w-8 h-8 text-[#19bfb7]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm text-gray-700">{uploadedFile.name}</span>
                  <button onClick={removeFile} className="text-red-500 hover:text-red-700">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ) : (
                <>
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <label className="cursor-pointer">
                    <span className="text-[#19bfb7] hover:text-[#18ada6] font-medium">Click to upload</span>
                    <span className="text-gray-500"> or drag and drop</span>
                    <input type="file" className="hidden" accept=".svg,.png,.jpg,.jpeg,.gif,.pdf" onChange={handleFileInput} />
                  </label>
                  <p className="text-xs text-gray-400 mt-2">SVG, PNG, JPG or GIF (max. 800×400px)</p>
                </>
              )}
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Client</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Add a client <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.clientId}
                    onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                    className="w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent"
                  >
                    <option value="">Choose client</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name || client.company_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Role <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.roleId}
                    onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
                    disabled={!formData.clientId}
                    className="w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#19bfb7] focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Choose role</option>
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.title || role.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Formatting Options</h3>
              <div className="space-y-3">
                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-700">Hide contact details</span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, hideContactDetails: !formData.hideContactDetails })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.hideContactDetails ? "bg-[#19bfb7]" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.hideContactDetails ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </label>

                <label className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-gray-700">Include shortlist link</span>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, includeShortlistLink: !formData.includeShortlistLink })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      formData.includeShortlistLink ? "bg-[#19bfb7]" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        formData.includeShortlistLink ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col">
          <div className="flex-1 bg-gray-100 rounded-xl overflow-hidden">
            {applicant ? (
              <div className="h-full flex items-center justify-center p-8">
                <div className="text-center">
                  <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-200 flex items-center justify-center">
                    <span className="text-2xl font-semibold text-gray-600">
                      {(applicant.username || "C")[0].toUpperCase()}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {applicant.username || "Candidate"}
                  </h3>
                  <p className="text-sm text-gray-500">{applicant.job?.title || "Applicant"}</p>
                  {applicant.job?.company && (
                    <p className="text-xs text-gray-400 mt-1">{applicant.job.company}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full min-h-[300px] flex items-center justify-center">
                <img
                  src={CandidateSubmissionImage}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-6 justify-end">
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !formData.clientId || !formData.roleId}
              className="px-6 py-2.5 text-sm font-medium text-white bg-[#19bfb7] rounded-lg hover:bg-[#18ada6] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending..." : "Send"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateToClientSubmission;